/**
 * <FeatureGate> — render children only when the given feature is visible to
 * the current user.
 *
 * Usage:
 *   <FeatureGate feature="route.dashboard">
 *     <NavLink to="/">Dashboard</NavLink>
 *   </FeatureGate>
 *
 * Optional `fallback` prop renders when the feature is hidden (default: null).
 * Use `fallback={<UpsellCta />}` for paywalled features once an upsell UI lands.
 * For MVP, hidden features are simply absent from the UI — no upsell.
 */

import type { ReactNode } from 'react';
import { useFeatureVisibility } from '@/hooks/useFeatureVisibility';

interface FeatureGateProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureGate({ feature, children, fallback = null }: FeatureGateProps) {
  const { isVisible } = useFeatureVisibility();
  if (!isVisible(feature)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}
